<script setup>
/* eslint semi: 1 */
import { ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { resetPassword as AmplifyResetPassword } from 'aws-amplify/auth';

import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import { t } from '@/i18n/i18ns';
import CONSTANTS from '@/globals/constants';

// Component imports
import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

const cognitoAuthStore = useCognitoAuthStore();

defineOptions({
  mixins: [AuthMixin]
});

const props = defineProps({
  isPageMini: {
    type: Boolean,
    default: false
  },
  prefillEmail: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['successSubmit']);

const schema = toTypedSchema(yup.object({
  email: yup.string()
    .email('label.invalidEmail')
    .transform((value) => value?.toLowerCase?.().trim?.() ?? value)
    .required('label.requiredEmail'),
}));

const {
  errors,
  handleSubmit,
  isSubmitting,
  defineField,
  setFieldError,
  setFieldValue
} = useForm({
  validationSchema: schema,
});

const [email] = defineField('email');
const contactSystemAdmin = ref(false);

watch(() => props.prefillEmail, (newValue) => {
  if (newValue) {
    setFieldValue('email', newValue);
  }
}, {
  immediate: true
});

const performForgotPassword = async (username) => {
  const { error } = await cognitoAuthStore.getProviderInfo(username);
  if (error) {
    return { error, success: false };
  }

  try {
    await useCognitoAuthStore().configureWithEmail(username);
    await AmplifyResetPassword({ username });
  } catch (error) {
    console.error('Error performing forgot password:', error);
    return { error, success: false };
  }

  return { error: null, success: true };
};

const onSubmit = handleSubmit(async (values, actions) => {
  // Handle form submission
  actions.setFieldValue('email', values.email);

  const { error } = await performForgotPassword(values.email);
  if (error) {
    if (
      error.message.includes('expression pattern') ||
      error.name === CONSTANTS.USER_NOT_AUTHORIZED ||
      (error.name === CONSTANTS.USER_NOT_FOUND && error.message.includes('UserMigration failed'))
    ) {
      setFieldError('email', 'label.invalidEmail');
    } else if (error.message.includes('cannot be reset in the current state')) {
      contactSystemAdmin.value = true;
      setFieldError('email', 'errors.invalid.passwordReset');
    } else if (error.message.includes('Cannot reset password for the user as there is no registered')) {
      setFieldError('email', 'errors.invalid.passwordReset');
    } else if (
      error.name === 'LimitExceededException' ||
      error.message.includes('Attempt limit exceeded')
    ) {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('label.passwordAttemptLimitExceeded')
        },
        callbacks: {
          eventOnClose: () => {}
        }
      });
    } else {
      setFieldError('email', error.message);
    }

    return;
  }

  emit('successSubmit', values.email);
});
</script>

<template>
  <form
    class="max-[1919px]:my-3 min-[1920px]:my-5 flex flex-col w-full h-full"
    data-component-element-id="forgot-password-email-form"
    @submit="onSubmit"
  >
    <template v-if="contactSystemAdmin">
      <div class="text-white text-sm uppercase max-[1919px]:pt-4 min-[1920px]:pt-10 flex-1">
        <p>{{$t('label.passwordCannotBeReset')}}</p>
        <p>{{$t('label.conctactSystemAdmin')}}</p>
      </div>

      <form-button
        type="submit"
        buttonTheme="light"
        data-component-element-id="forgot-password-go-back"
        :size="isPageMini ? 'xs' : 'md'"
        :loading="isSubmitting"
        :disabled="isSubmitting"
        :label="$t('label.goBack')"
        @click="contactSystemAdmin = false"
      />
    </template>

    <template v-else>
      <div class="flex flex-col flex-1 justify-center mb-8 gap-4">
        <div class="max-[1919px]:py-2 min-[1920px]:py-4 max-[1919px]:text-xs min-[1920px]:text-md text-white text-center">
          <span>{{ $t('label.resetPasswordDescription') }}</span>
        </div>

        <form-input
          id="email"
          v-model="email"
          type="email"
          autocomplete="username"
          :label="$t('label.email')"
          inputTheme="light"
          :size="isPageMini ? 'xs' : 'md'"
          required
          :disabled="isSubmitting"
          :validationStatus="errors.email ? 'error' : undefined"
          data-component-element-id="forgot-password-email-input"
        >
          <template
            v-if="errors?.email"
            #validationTransparent
          >
            {{ $t(errors.email) }}
          </template>
        </form-input>
      </div>

      <form-button
        type="submit"
        buttonTheme="light"
        data-component-element-id="forgot-password-email-submit"
        :size="isPageMini ? 'xs' : 'md'"
        :loading="isSubmitting"
        :disabled="isSubmitting"
        :label="$t('label.sendCode')"
        @click="onSubmit"
      />
    </template>
  </form>
</template>
